import { computed, Injectable, signal } from '@angular/core';
import { FloatingAlert, FloatingAlertAction, FloatingAlertType } from '@shared/components/floating-alerts/model/floating-alerts.model';
import { interval, take } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FloatingAlertsService {
  private readonly _list = signal<FloatingAlert[]>([]);
  public readonly list = computed(() => this._list());

  /**
   * Method to create a new floating alert of type info
   * @param message string to display, could be a simple string or a translation identifier
   * @param actions actions' array to show on the alert, the "Close" action should not be included, as it will always be there.
   * @param hideTime time for the alert to hide in seconds, defaults to 10 seconds
   */
  info(message: string, actions: FloatingAlertAction[] = [], hideTime: number = 10): string {
    return this._create('info', message, actions, hideTime);
  }

  /**
   * Method to create a new floating alert of type error
   * @param message string to display, could be a simple string or a translation identifier
   * @param actions actions' array to show on the alert, the "Close" action should not be included, as it will always be there.
   * @param hideTime time for the alert to hide in seconds, defaults to 10 seconds
   */
  error(message: string, actions: FloatingAlertAction[] = [], hideTime: number = 10): string {
    return this._create('error', message, actions, hideTime);
  }

  /**
   * Method to create a new floating alert
   * @param type type of the alert @see FloatingAlertType
   * @param message string to display, could be a simple string or a translation identifier
   * @param actions actions' array to show on the alert, the "Close" action should not be included, as it will always be there.
   * @param hideTime time for the alert to hide in seconds
   * @private
   */
  private _create(type: FloatingAlertType, message: string, actions: FloatingAlertAction[], hideTime: number): string {
    const id = crypto.randomUUID();
    this._list.update((arr) => [
      ...arr,
      {
        id,
        type,
        message,
        actions,
      },
    ]);
    interval(hideTime * 1000)
      .pipe(take(1))
      .subscribe(() => this.remove(id));
    return id;
  }

  /**
   * Remove a floating alert
   * @param id identifier of the alert to remove from the notifications
   */
  remove(id: string) {
    this._list.update((list) => list.filter((value) => value.id !== id));
  }
}
